import { useEffect, useRef, useState } from "react";

import { createProgram } from "../../preview/webgl";

/**
 * Mini canvas que compila un shader de transición y lo renderiza con dos
 * texturas de muestra (gradiente rojo→azul para A y azul→verde para B).
 *
 * - Al montar: renderiza un fotograma a progress=0.5 (preview estático)
 * - Al hover: anima progress 0→1 en loop hasta que el mouse sale
 */

const VERT = `#version 300 es
in vec2 a_pos;
out vec2 v_uv;
void main() {
  v_uv = a_pos * 0.5 + 0.5;
  v_uv.y = 1.0 - v_uv.y;
  gl_Position = vec4(a_pos, 0.0, 1.0);
}`;

function makeFrag(body: string): string {
  return `#version 300 es
precision highp float;
uniform sampler2D u_from;
uniform sampler2D u_to;
uniform float progress;
vec4 getFromColor(vec2 uv) { return texture(u_from, uv); }
vec4 getToColor(vec2 uv)   { return texture(u_to, uv); }
float rand(vec2 co) { return fract(sin(dot(co.xy, vec2(12.9898,78.233))) * 43758.5453); }
${body}
in vec2 v_uv;
out vec4 fragColor;
void main() { fragColor = transition(v_uv); }
`;
}

/** Para los IDs del shader compartido (numéricos), usamos un body GLSL que
 *  invoca un fade simple — el panel principal usará el compositor real para
 *  el preview de las xfade nativas. Acá solo previewmos los dinámicos. */

interface Props {
  id: string;
  glslBody: string;     // body con `vec4 transition(vec2 uv) { ... }`
  label: string;
  icon?: string;
  size?: number;
  onPick?: () => void;
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
}

// Texturas de muestra (gradientes coloreados) compartidas entre todos los thumbs
let _sharedTexA: WebGLTexture | null = null;
let _sharedTexB: WebGLTexture | null = null;

function makeGradient(gl: WebGL2RenderingContext, c1: [number, number, number], c2: [number, number, number]): WebGLTexture {
  const size = 64;
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const t = x / (size - 1);
      const idx = (y * size + x) * 4;
      data[idx]     = Math.round((c1[0] * (1 - t) + c2[0] * t) * 255);
      data[idx + 1] = Math.round((c1[1] * (1 - t) + c2[1] * t) * 255);
      data[idx + 2] = Math.round((c1[2] * (1 - t) + c2[2] * t) * 255);
      data[idx + 3] = 255;
    }
  }
  const tex = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, size, size, 0, gl.RGBA, gl.UNSIGNED_BYTE, data);
  return tex;
}

function ensureSharedTextures(gl: WebGL2RenderingContext): { a: WebGLTexture; b: WebGLTexture } {
  if (!_sharedTexA) _sharedTexA = makeGradient(gl, [0.9, 0.4, 0.3], [0.95, 0.85, 0.2]);
  if (!_sharedTexB) _sharedTexB = makeGradient(gl, [0.2, 0.5, 0.95], [0.4, 0.95, 0.85]);
  return { a: _sharedTexA, b: _sharedTexB };
}

export default function TransitionThumb({
  glslBody, label, icon, size = 64, onPick, draggable, onDragStart,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [hovering, setHovering] = useState(false);
  const animRef = useRef<number | null>(null);
  const errRef = useRef<string | null>(null);
  const programRef = useRef<WebGLProgram | null>(null);

  function ensureProgram(gl: WebGL2RenderingContext): WebGLProgram | null {
    if (programRef.current) return programRef.current;
    try {
      const p = createProgram(gl, VERT, makeFrag(glslBody));
      programRef.current = p;
      // Setup VBO + attribs + uniforms
      const buf = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buf);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, 1,1]), gl.STATIC_DRAW);
      gl.useProgram(p);
      const aPos = gl.getAttribLocation(p, "a_pos");
      gl.enableVertexAttribArray(aPos);
      gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);
      const { a, b } = ensureSharedTextures(gl);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, a);
      gl.activeTexture(gl.TEXTURE2);
      gl.bindTexture(gl.TEXTURE_2D, b);
      gl.uniform1i(gl.getUniformLocation(p, "u_from"), 0);
      gl.uniform1i(gl.getUniformLocation(p, "u_to"), 2);
      return p;
    } catch (e: any) {
      errRef.current = e?.message || String(e);
      return null;
    }
  }

  function renderAt(progress: number) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const gl = canvas.getContext("webgl2");
    if (!gl) return;
    const p = ensureProgram(gl);
    if (!p) return;
    gl.useProgram(p);
    gl.uniform1f(gl.getUniformLocation(p, "progress"), Math.max(0, Math.min(1, progress)));
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.clearColor(0, 0, 0, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }

  useEffect(() => {
    renderAt(0.5);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [glslBody]);

  // Hover: loop animado 0..1..0
  useEffect(() => {
    if (!hovering) {
      renderAt(0.5);
      return;
    }
    let t0 = performance.now();
    function tick(now: number) {
      const elapsed = (now - t0) / 1400;  // 1.4s un ciclo
      const cycle = elapsed % 2;
      const prog = cycle < 1 ? cycle : 2 - cycle;
      renderAt(prog);
      animRef.current = requestAnimationFrame(tick);
    }
    animRef.current = requestAnimationFrame(tick);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hovering]);

  return (
    <div
      draggable={draggable}
      onDragStart={onDragStart}
      onClick={onPick}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        gap: 4, padding: 6, borderRadius: 6, cursor: onPick ? "pointer" : (draggable ? "grab" : "default"),
        background: "var(--bg-elev-2)",
        border: "1px solid var(--border)",
        minWidth: size + 12,
      }}
      title={label}
    >
      <canvas
        ref={canvasRef}
        width={size} height={Math.round(size * 9 / 16)}
        style={{ borderRadius: 3, background: "#000", pointerEvents: "none" }}
      />
      <span style={{ fontSize: 10, color: "var(--text-dim)", textAlign: "center",
                     maxWidth: size + 8, whiteSpace: "nowrap",
                     overflow: "hidden", textOverflow: "ellipsis" }}>
        {icon ? `${icon} ` : ""}{label}
      </span>
    </div>
  );
}
